/*
 * ZoomBox: A custom element for interactive image zoom/pan on mouse/touch
 * Usage: Wrap an <img> with <zoom-box> and optionally set data-scale, etc.
 * Handles mouse hover, wheel zoom, pan, and touch gestures for product images.
 */

/**
 * Default configuration for wheel zoom behavior
 * @type {Object}
 */
const Default = {
  initialZoom: 3, // Default zoom level when zooming in
  minZoom: 1.25, // Minimum allowed zoom
  maxZoom: 4, // Maximum allowed zoom
  zoomSpeed: 0.01, // How fast zoom changes with wheel
};

/**
 * ZoomBox custom element class
 * @extends HTMLElement
 */
class ZoomBox extends HTMLElement {
  /**
   * Constructor for ZoomBox
   * @param {Object} config - Configuration object for zoom behavior
   */
  constructor(config = {}) {
    super();
    // Find the image inside this zoom-box
    this.img = this.querySelector("img");
    // Scale factor from data attribute or default
    this.scale = this.dataset.scale || 2;
    // Initial (no zoom) scale
    this.initScale = 1;
    // Track zoom state
    this.isZoomedIn = false;
    // Set up image size and config
    this.init();

    // Set up wheel event behavior
    // Merge user config with defaults
    this.config = this._mergeConfig(config);

    // Clamp initial zoom to allowed range
    const { initialZoom, minZoom, maxZoom } = this.config;

    this.initialZoom = Math.max(Math.min(initialZoom, maxZoom), minZoom);
    this.zoom = this.initialZoom;
  }

  /**
   * Expose default config for extension
   * @returns {Object} Default configuration
   */
  static get Default() {
    return Default;
  }

  /**
   * Initialize image size based on container
   */
  init() {
    if (!this.img) return;

    // Set image to fill container at base scale
    const rect = this.getBoundingClientRect();
    let currentWidth = rect.width * this.initScale;
    let currentHeight = rect.height * this.initScale;
    this.img.style.width = `${currentWidth}px`;
    this.img.style.height = `${currentHeight}px`;

    // Prevent drag ghost
    this.img.draggable = false;
  }

  /**
   * Merge user config with defaults
   * @param {Object} config - Configuration object for zoom behavior
   * @returns {Object} Merged configuration
   */
  _mergeConfig(config) {
    return {
      ...this.constructor.Default,
      ...(typeof config === "object" ? config : {}),
    };
  }

  /**
   * Mouse enters: activate zoom
   */
  _handleMouseover() {
    if (this.isZoomedIn) return;
    this.isZoomedIn = true;
    if (!this.classList.contains("zoomed-in")) {
      this.classList.add("zoomed-in");
    }
    this.style.setProperty("--zoom", this.scale.toString());
  }

  /**
   * Mouse moves: update zoom origin
   * @param {PointerEvent} e - Pointer event object
   */
  _handleMousemove(e) {
    if (!this.isZoomedIn) return;

    // Calculate mouse position as % of box
    const rect = this.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const percentageX = `${(x * 100) / rect.width}%`;
    const percentageY = `${(y * 100) / rect.height}%`;

    // Set CSS vars for transform-origin
    this.style.setProperty("--zoom-pos-x", percentageX);
    this.style.setProperty("--zoom-pos-y", percentageY);
  }

  /**
   * Mouse leaves: reset zoom
   */
  _handleMouseout() {
    if (!this.isZoomedIn) return;

    // Reset zoom and state
    this.style.setProperty("--zoom", this.initScale.toString());
    if (this.classList.contains("zoomed-in")) {
      this.classList.remove("zoomed-in");
    }
    this.isZoomedIn = false;
  }

  /**
   * Mouse wheel: change zoom level
   * @param {WheelEvent} e - Wheel event object
   */
  _handleWheel(e) {
    if (!this.isZoomedIn) return;

    // Prevent scroll
    e.preventDefault();

    // Calculate new zoom and clamp
    const newZoom = this.zoom + e.deltaY * (this.config.zoomSpeed * -1);
    const { minZoom, maxZoom } = this.config;
    this.zoom = Math.max(Math.min(newZoom, maxZoom), minZoom);

    // Update CSS var for scale
    this.style.setProperty("--zoom", this.zoom.toString());
  }

  /**
   * Touch start: treat as zoom in
   * @param {TouchEvent} e - Touch event object
   */
  _handleTouchstart(e) {
    e.preventDefault();
    // Simulate mouseover for touch
    this._handleMouseover();
  }

  /**
   * Touch move: update zoom origin
   * @param {TouchEvent} e - Touch event object
   */
  _handleTouchmove(e) {
    if (!this.isZoomedIn) return;

    // Calculate touch as % of box
    const rect = this.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const y = e.touches[0].clientY - rect.top;

    let percentageX = (x * 100) / rect.width;
    let percentageY = (y * 100) / rect.height;

    percentageX = Math.max(Math.min(percentageX, 100), 0);
    percentageY = Math.max(Math.min(percentageY, 100), 0);

    // Set CSS vars for transform-origin
    this.style.setProperty("--zoom-pos-x", `${percentageX}%`);
    this.style.setProperty("--zoom-pos-y", `${percentageY}%`);
  }

  /**
   * Touch end: reset zoom
   */
  _handleTouchend() {
    this._handleMouseout();
  }

  /**
   * Lifecycle: set up all event listeners for mouse/touch
   */
  connectedCallback() {
    if (!this.img) return;
    // Bind handlers once and store on instance for removal
    this._onMouseover = this._handleMouseover.bind(this);
    this._onMousemove = this._handleMousemove.bind(this);
    this._onMouseout = this._handleMouseout.bind(this);
    this._onWheel = this._handleWheel.bind(this);
    this._onTouchstart = this._handleTouchstart.bind(this);
    this._onTouchmove = this._handleTouchmove.bind(this);
    this._onTouchend = this._handleTouchend.bind(this);

    // Mouse events
    this.addEventListener("mouseover", this._onMouseover);
    this.addEventListener("mousemove", this._onMousemove);
    this.addEventListener("mouseleave", this._onMouseout);
    this.addEventListener("wheel", this._onWheel);
    // Touch events
    this.addEventListener("touchstart", this._onTouchstart);
    this.addEventListener("touchmove", this._onTouchmove);
    this.addEventListener("touchend", this._onTouchend);
  }

  disconnectedCallback() {
    // Remove all event listeners using the same handler references set in connectedCallback
    this.removeEventListener("mouseover", this._onMouseover);
    this.removeEventListener("mousemove", this._onMousemove);
    this.removeEventListener("mouseleave", this._onMouseout);
    this.removeEventListener("wheel", this._onWheel);
    this.removeEventListener("touchstart", this._onTouchstart);
    this.removeEventListener("touchmove", this._onTouchmove);
    this.removeEventListener("touchend", this._onTouchend);
  }
}

// Register the custom element for use in HTML
customElements.define("zoom-box", ZoomBox);
